import React, { useState, useEffect } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { urls } from '../../../urls';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import { AppDataGrid } from '@/components/ui';
import { Loader } from '@/components/loader/loader';
import { AppCard } from '@/components/app-card/app-card';
import { formatDate } from '@/lib/formatters';
import { GridAlignment } from '@mui/x-data-grid';
import { BudgetChangeModal } from './budget-change-modal';
import { getUser } from '@/lib/common';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { canUserDecideBudgetChange } from '../budget-approval';
import { Pill } from '@/components/ds';
import { statusTone } from '@/theme/tokens';

const columns = [
  {
    field: 'budget_type',
    headerName: 'Budget Type',
    align: 'left' as GridAlignment,
    flex: 0.7,
  },
  {
    field: 'name',
    headerName: 'Budget Name',
    align: 'left' as GridAlignment,
    flex: 1,
    valueGetter: (value: any) => {
      if (value.row.budget_type === 'Global') {
        return 'Global';
      } else if (value.row.budget_type === 'Department') {
        return value.row.department_name;
      } else if (value.row.budget_type === 'System') {
        return value.row.system_name;
      }
      return '';
    },
  },
  { field: 'requester', headerName: 'Requester', flex: 0.7 },
  {
    field: 'change_timestamp',
    headerName: 'Date',
    flex: 0.7,
    valueGetter: (value: any) => {
      if (!value.row.change_timestamp) return '';
      return formatDate(value.row.change_timestamp);
    },
  },
  {
    field: 'null',
    headerName: 'Current value',
    align: 'left' as GridAlignment,
    headerAlign: 'left' as GridAlignment,
    type: 'string',
    valueGetter: (value: any) =>
      `${formatCurrencyNoUnit(value.row.original_currency_value)} ${value.row.original_currency}`,
    flex: 1,
  },
  {
    field: 'null2',
    headerName: 'New Value',
    align: 'left' as GridAlignment,
    headerAlign: 'left' as GridAlignment,
    type: 'string',
    valueGetter: (value: any) =>
      `${formatCurrencyNoUnit(value.row.change_amount)} ${value.row.original_currency}`,
    flex: 1,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 0.7,
    align: 'center' as GridAlignment,
    headerAlign: 'center' as GridAlignment,
    renderCell: (params: any) => {
      const value = params.row.status;

      return (
        <div
          style={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
          <Pill tone={statusTone(value)}>{value}</Pill>
        </div>
      );
    },
  },
];

interface BudgetChangeRecord {
  id: number;
  budget_type: string;
  name: string;
  department_name?: string;
  system_name?: string;
  department_owner_id?: number | null;
  created_by: number;
  requester: string;
  change_timestamp: string;
  original_currency_value: number;
  original_currency: string;
  change_amount: number;
  status: string;
  reason?: string;
  id_approver?: number;
  approver_comment?: string;
}

function BudgetChangeApproval() {
  const [budgets, setBudgets] = useState<BudgetChangeRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedBudgetChange, setSelectedBudgetChange] =
    useState<BudgetChangeRecord | null>(null);

  const user = getUser();
  const currentUserIsAdmin = Boolean(user?.is_admin);
  const currentUserIsSponsor = Boolean(user?.is_sponsor);

  const canDecideSelected =
    selectedBudgetChange && user?.id
      ? canUserDecideBudgetChange(
          {
            id: user.id,
            is_admin: currentUserIsAdmin,
            is_sponsor: currentUserIsSponsor,
          },
          selectedBudgetChange,
        )
      : false;

  const fetchBudgetChanges = async () => {
    setLoading(true);

    const data = await fetchWrapper.get(urls.DevOps.getPendingBudgetChangesV2);
    setBudgets(data);

    setLoading(false);
  };

  useEffect(() => {
    fetchBudgetChanges();
  }, []);

  const handleRowClick = (params: any) => {
    const budgetChange = params.row as BudgetChangeRecord;

    setSelectedBudgetChange(budgetChange);
    setDetailsModalOpen(true);
  };

  const getBudgetTypeFromRecord = (
    budgetChange: BudgetChangeRecord,
  ): 'global' | 'department' | 'system' => {
    const type = budgetChange.budget_type.toLowerCase();
    if (type === 'global') return 'global';
    if (type === 'department') return 'department';
    if (type === 'system') return 'system';
    return 'global';
  };

  const getEntityName = (budgetChange: BudgetChangeRecord): string => {
    if (budgetChange.budget_type === 'Global') return 'Global';
    if (budgetChange.budget_type === 'Department')
      return budgetChange.department_name || '';
    if (budgetChange.budget_type === 'System')
      return budgetChange.system_name || '';
    return '';
  };

  const handleApprove = async (comment: string) => {
    if (!selectedBudgetChange) return;

    try {
      await fetchWrapper.post(urls.DevOps.decideBudgetChangeV2(selectedBudgetChange.id), {
        action: 'approve',
        approverId: user?.id,
        comment,
      });

      await fetchBudgetChanges();
      setDetailsModalOpen(false);
      setSelectedBudgetChange(null);
    } catch (error) {
      console.error('Error approving budget change:', error);
    }
  };

  const handleReject = async (comment: string) => {
    if (!selectedBudgetChange) return;

    try {
      await fetchWrapper.post(urls.DevOps.decideBudgetChangeV2(selectedBudgetChange.id), {
        action: 'reject',
        approverId: user?.id,
        comment,
      });

      await fetchBudgetChanges();
      setDetailsModalOpen(false);
      setSelectedBudgetChange(null);
    } catch (error) {
      console.error('Error rejecting budget change:', error);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const statusSummary = [
    { label: 'Pending', count: budgets.filter((b) => b.status === 'Pending').length },
    { label: 'Approved', count: budgets.filter((b) => b.status === 'Approved').length },
    { label: 'Rejected', count: budgets.filter((b) => b.status === 'Rejected').length },
  ];

  return (
    <AppCard
      className="card100"
      title="Budget Changelog"
      icon={<BarChartOutlinedIcon color="primary" />}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 14,
        }}>
        <Pill tone="neutral">{budgets.length} total</Pill>
        {statusSummary.map((s) => (
          <Pill key={s.label} tone={statusTone(s.label)}>
            {s.count} {s.label}
          </Pill>
        ))}
      </div>

      <AppDataGrid
        style={{ float: 'left' }}
        onRowClick={handleRowClick}
        rows={budgets}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
      />

      {selectedBudgetChange && (
        <BudgetChangeModal
          open={detailsModalOpen}
          onClose={() => {
            setDetailsModalOpen(false);
            setSelectedBudgetChange(null);
          }}
          budgetType={getBudgetTypeFromRecord(selectedBudgetChange)}
          currentBudget={selectedBudgetChange.original_currency_value}
          budgetCurrency={selectedBudgetChange.original_currency}
          entityName={getEntityName(selectedBudgetChange)}
          readOnly={true}
          status={selectedBudgetChange.status}
          requestedBudget={selectedBudgetChange.change_amount}
          changeReason={selectedBudgetChange.reason || 'No reason provided'}
          requestDate={formatDate(selectedBudgetChange.change_timestamp)}
          requesterName={selectedBudgetChange.requester}
          isRequest={selectedBudgetChange.budget_type !== 'Global'}
          approverId={selectedBudgetChange.id_approver}
          requiredDepartmentOwnerId={
            selectedBudgetChange.budget_type === 'System'
              ? Number(selectedBudgetChange.department_owner_id ?? NaN)
              : undefined
          }
          currentUserId={user?.id}
          currentUserIsAdmin={currentUserIsAdmin}
          currentUserIsSponsor={currentUserIsSponsor}
          canDecide={canDecideSelected}
          onApprove={handleApprove}
          onReject={handleReject}
          existingApproverComment={selectedBudgetChange.approver_comment}
        />
      )}
    </AppCard>
  );
}

export default BudgetChangeApproval;
